import { createStyles } from "@mantine/core";

const bgColor = "var(--app-surface)";

const useStyles = createStyles((theme) => ({
  addMedicationModal: {
    "& .mantine-Drawer-header": { display: "none" },
    "& .mantine-Drawer-root": { overflow: "initial" },
    "& .mantine-Paper-root": {
      overflow: "hidden",
      backgroundColor: bgColor,
      boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
      display: "flex",
      flexDirection: "column",
      height: "100%",
    },
    "& .mantine-Drawer-body": { padding: 0, height: "100%" },
    "& .mantine-Input-input": { borderRadius: "8px" },
    "& .mantine-InputWrapper-required": { color: "var(--app-text)" },
    "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
      { WebkitAppearance: "none", margin: 0 },
    // Prevent iOS Safari from auto-zooming when inputs are focused.
    // iOS zooms any input whose font-size is below 16px; setting it to
    // exactly 16px on mobile disables the zoom without affecting desktop.
    [theme.fn.smallerThan("sm")]: {
      "& .mantine-Input-input, & .mantine-Textarea-input": {
        fontSize: "16px",
      },
    },
  },

  modalBody: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },

  modalTopBar: {
    width: "100%",
    height: "64px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0 24px",
    borderBottom: "1px solid var(--app-border)",
    backgroundColor: "var(--app-surface)",
    borderRadius: "16px 16px 0 0",
    flexShrink: 0,
    [theme.fn.smallerThan("sm")]: {
      height: "52px",
      padding: "0 14px",
      borderRadius: "0",
    },
  },

  modalTitle: {
    margin: 0,
    fontSize: "24px",
    fontWeight: 700,
    color: "var(--app-text)",
    fontFamily: "Inter",
    [theme.fn.smallerThan("sm")]: {
      fontSize: "17px",
    },
  },

  modalCloseBtn: {
    background: "none",
    border: "none",
    fontSize: "22px",
    cursor: "pointer",
    color: "var(--app-text-muted)",
    lineHeight: 1,
    padding: "4px 8px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
  },

  clientCard: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    backgroundColor: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: "12px",
    padding: "12px 18px",
    marginBottom: "24px",
    [theme.fn.smallerThan("sm")]: {
      padding: "10px 12px",
      marginBottom: "16px",
      gap: "10px",
    },
  },

  clientAvatar: {
    width: "46px",
    height: "46px",
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
    border: "2px solid var(--app-border)",
    [theme.fn.smallerThan("sm")]: {
      width: "36px",
      height: "36px",
    },
  },

  clientInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  clientName: {
    margin: 0,
    fontSize: "15px",
    fontWeight: 600,
    color: "var(--app-text)",
    [theme.fn.smallerThan("sm")]: { fontSize: "13px" },
  },

  clientId: {
    margin: 0,
    fontSize: "13px",
    color: "var(--app-text-muted)",
    [theme.fn.smallerThan("sm")]: { fontSize: "11px" },
  },

  formBox: {
    width: "100%",
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    backgroundColor: "var(--app-surface-2)",
    touchAction: "pan-x pan-y pinch-zoom",
  },

  wrapper: {
    width: "100%",
    padding: "20px 20px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    [theme.fn.smallerThan("sm")]: {
      padding: "14px 10px",
      gap: 10,
    },
  },

  medContainer: {
    background: "var(--app-surface-2)",
    borderRadius: 16,
    border: "1px solid var(--app-border)",
    padding: "32px 13px",
    [theme.fn.smallerThan("sm")]: {
      padding: "16px 10px",
      borderRadius: 10,
    },
  },

  topFieldsBox: {
    width: "100%",
    display: "flex",
    gap: "17px",
    overflow: "hidden",
    "& .isPrescribed": { flex: 1 },
    "& .isNarcotic": {
      flex: 1,
      "@media (max-width:420px)": { flexDirection: "column" },
    },
    "& .mantine-1ejqehl": { display: "flex", flexDirection: "column" },
    "& .mantine-1cu6yj8:checked": {
      background: "var(--app-primary)",
      borderColor: "var(--app-primary)",
    },
    "& .mantine-Radio-radio:checked": {
      background: "var(--app-primary)",
      borderColor: "var(--app-primary)",
    },
    [theme.fn.smallerThan("sm")]: {
      flexDirection: "column",
      gap: "12px",
      "& .isPrescribed": {
        borderRight: "none",
        borderBottom: "1px solid var(--app-border)",
        paddingBottom: "12px",
      },
    },
  },

  inputStyle: {
    flex: 1,
    "& .mantine-Input-wrapper": {
      height: "auto",
      backgroundColor: "var(--app-surface)",
      borderRadius: "8px",
      border: "1px solid var(--app-border)",
      boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
      "& .mantine-Input-input": {
        fontFamily: "Inter",
        border: "none",
        backgroundColor: "transparent",
        padding: "10px 14px",
        height: "44px",
        color: "var(--app-text)",
      },
      "& .mantine-MultiSelect-input": {
        minHeight: "44px",
        maxHeight: "fit-content",
        height: "100%",
        padding: "0px",
        "& .mantine-MultiSelect-values": {
          minHeight: "inherit",
          padding: "10px 36px 10px 14px",
          "& .mantine-MultiSelect-searchInput": {
            minHeight: "100%",
            height: "100%",
          },
        },
      },
      "& .mantine-Textarea-input": { height: 100, overflowY: "auto" },
      "& .mantine-Select-rightSection svg, .mantine-MultiSelect-rightSection svg":
        { color: "var(--app-text-muted)" },
    },
    "& .mantine-DateInput-wrapper, .mantine-DatePickerInput-wrapper": {
      "& .mantine-DateInput-icon, .mantine-DatePickerInput-icon": {
        "& svg": { color: "var(--app-text)", fontSize: 18 },
      },
      "& .mantine-DateInput-input, .mantine-DatePickerInput-input": {
        paddingLeft: 36,
      },
    },
    "& .mantine-InputWrapper-label": {
      color: "var(--app-text)",
      marginBottom: "6px",
      fontFamily: "Inter",
      fontSize: "14px",
      fontWeight: 500,
    },
  },

  radioGroupWrapper: {
    "& .mantine-InputWrapper-label": {
      color: "var(--app-text)",
      marginBottom: "6px",
      fontFamily: "Inter",
      fontSize: "14px",
      fontWeight: 500,
    },
    "& .mantine-Radio-root": { flex: 1 },
  },

  radioGroup: {
    border: "1px solid var(--app-border)",
    backgroundColor: "var(--app-surface-2)",
    width: "100%",
    height: "auto",
    borderRadius: "10px",
    padding: "14px 20px",
    "& .mantine-Radio-radio:checked": {
      background: "var(--app-primary)",
      borderColor: "var(--app-primary)",
    },
    [theme.fn.smallerThan("sm")]: {
      padding: "10px 12px",
    },
  },

  disabledLabel: { color: "gray" },
  normalLabel: { color: "unset" },

  formRow: {
    width: "100%",
    display: "flex",
    gap: "17px",
    marginBlock: "17px",
    [theme.fn.smallerThan("sm")]: {
      flexDirection: "column",
      gap: "12px",
      marginBlock: "12px",
    },
  },

  medicationDosageBox: {
    width: "100%",
    marginBottom: "17px",
    backgroundColor: "var(--app-surface-2)",
    borderRadius: "8px",
    padding: "18px 14px",
    border: "1px solid var(--app-border)",
    "& > p": {
      color: "var(--app-text)",
      margin: "0 0 12px 0",
      fontSize: "16px",
      fontWeight: 600,
      fontFamily: "Inter",
    },
    [theme.fn.smallerThan("sm")]: {
      padding: "12px 10px",
      marginBottom: "12px",
      "& > p": { fontSize: "14px" },
    },
  },

  medicationDosageInner: {
    width: "100%",
    display: "flex",
    gap: "15px",
    flexWrap: "wrap",
    "& > div": { flex: "1 1 120px", minWidth: 0 },
    [theme.fn.smallerThan("sm")]: {
      flexDirection: "column",
      gap: "12px",
      "& > div": { flex: "unset", width: "100%" },
    },
  },

  bottomFields: {
    position: "relative",
    backgroundColor: bgColor,
    width: "100%",
    marginBottom: "20px",
    "& input": { backgroundColor: bgColor },
    "& .mantine-Input-input": { backgroundColor: bgColor },
    "& > p": {
      margin: "0",
      color: "var(--app-text)",
      fontSize: "18px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "25px",
      marginLeft: "30px",
    },
    "& .dosageHeading": {
      position: "absolute",
      top: -12,
      left: 30,
      zIndex: 100,
      color: "var(--app-text)",
      margin: 0,
      display: "inline-block",
    },
  },

  bottomFieldsInner: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "15px",
    padding: "5px 15px 10px 15px",
    borderRadius: "5px",
    "& > div": { flex: "1 1 0", minWidth: 0 },
    "& .mantine-Input-input": { backgroundColor: bgColor },
    "& .mantine-InputWrapper-label": { marginTop: "10px" },
    [theme.fn.smallerThan("sm")]: {
      flexDirection: "column",
      padding: "5px 8px 10px 8px",
    },
  },

  forLabel: {
    "& .mantine-InputWrapper-label": { marginTop: "10px" },
    "& .mantine-Input-input": {
      backgroundColor: "var(--app-surface)",
      borderRadius: "8px",
      border: "1px solid var(--app-border)",
    },
  },

  saveButton: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
    padding: "14px 22px",
    backgroundColor: "var(--app-surface)",
    borderTop: "1px solid var(--app-border)",
    flexShrink: 0,
    [theme.fn.smallerThan("sm")]: {
      padding: "12px 14px",
      gap: "8px",
    },
  },

  cancelButton: {
    background: "transparent",
    color: "var(--app-text)",
    borderRadius: "8px",
    padding: "10px 18px",
    fontSize: "14px",
    fontWeight: 600,
    border: "none",
    fontFamily: "Inter",
    cursor: "pointer",
    "&:hover": { background: "var(--app-surface-2)" },
  },

  submitButton: {
    borderRadius: "8px",
    background: "var(--app-primary)",
    border: "1px solid var(--app-primary)",
    color: "var(--app-on-primary)",
    padding: "10px 22px",
    fontSize: "14px",
    fontWeight: 600,
    fontFamily: "Inter",
    cursor: "pointer",
    boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
    transition: "background .12s ease",
    "&:hover": { background: "var(--app-primary)", borderColor: "var(--app-primary)" },
    "&:disabled": { background: "var(--app-border)", borderColor: "var(--app-border)", color: "var(--app-text-muted)", cursor: "not-allowed", boxShadow: "none" },
  },

  uploadAndDateBox: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    [theme.fn.smallerThan("sm")]: { gap: "14px" },
  },

  dropzone: {
    "& .mantine-Dropzone-root": {
      background: "var(--app-surface)",
      border: "2px dashed var(--app-border)",
      borderRadius: "12px",
      padding: "20px",
    },
    "& .mantine-Group-root": {
      gap: "0px",
      minHeight: "initial !important",
    },
    width: "100%",
  },

  column: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    paddingBlock: "10px",
  },
  dragImage: {
    alignSelf: "center",
    width: "55px",
    height: "55px",
    opacity: 0.5,
    [theme.fn.smallerThan("sm")]: { width: "40px", height: "40px" },
  },
  uploadText: {
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: "500",
    lineHeight: "22px",
    margin: 0,
    textAlign: "center",
    "& span": { color: "var(--app-text)", textDecoration: "underline", cursor: "pointer" },
    [theme.fn.smallerThan("sm")]: { fontSize: "13px" },
  },
  uploadSubText: {
    fontSize: "12px",
    color: "var(--app-text-muted)",
    margin: 0,
    textAlign: "center",
    marginTop: "4px",
  },

  datePicker: {
    width: "100%",
    "& .mantine-DatePickerInput-input": {
      border: "1px solid var(--app-border)",
      borderRadius: "8px !important",
    },
    "& label": { fontSize: "0.875rem", fontWeight: 500 },
  },

  fileNameBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: "1px solid var(--app-border)",
    backgroundColor: "var(--app-surface)",
    borderRadius: "10px",
    padding: "12px 16px",
    marginTop: "10px",
    gap: "12px",
    [theme.fn.smallerThan("sm")]: { padding: "8px 10px", gap: "8px" },
  },
  fileNameLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
    minWidth: 0,
    [theme.fn.smallerThan("sm")]: { gap: "8px" },
  },
  fileNameInfo: { minWidth: 0 },
  fileNameText: {
    margin: 0,
    fontSize: "14px",
    fontWeight: 600,
    color: "var(--app-text)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    [theme.fn.smallerThan("sm")]: { fontSize: "12px" },
  },
  fileSizeText: {
    margin: 0,
    fontSize: "12px",
    color: "var(--app-text-muted)",
    [theme.fn.smallerThan("sm")]: { fontSize: "10px" },
  },
  fileNameActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
  },
  fileViewBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
  },

  adminScheduleBox: {
    "& .label": {
      fontSize: "0.875rem",
      fontWeight: 500,
      color: "var(--app-text)",
      wordBreak: "break-word",
      marginTop: "4px",
      margin: 0,
    },
    "& .mantine-Menu-dropdown": { padding: "1rem" },
    "& .heading": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
  },
  scheduleMenuBtnText: {
    marginTop: "10px",
    color: "var(--app-text)",
    cursor: "pointer",
    textAlign: "left",
    margin: 0,
    border: "1px solid var(--app-border)",
    borderRadius: "8px",
    padding: "14px 20px",
    [theme.fn.smallerThan("sm")]: { padding: "10px 14px" },
  },
  monthRepeatBox: {
    color: "var(--app-text)",
    fontSize: "14px",
    borderRadius: "15px",
    margin: "5px 0",
    background: "var(--app-surface)",
    height: "30px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  scheduleMenuBox: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    "& h2": { fontSize: "14px", textAlign: "center", margin: 0 },
    "& .footer": {
      display: "flex",
      justifyContent: "flex-end",
      "& button": { backgroundColor: "transparent" },
      "& button:nth-of-type(1)": { color: "var(--app-text)" },
      "& button:nth-of-type(2)": { color: "var(--app-primary)" },
    },
  },
  repeatEveryBox: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    "& > p": { color: "var(--app-text-muted)", fontWeight: 400, margin: 0 },
    "& .mantine-NumberInput-control": {
      border: "transparent",
      color: "var(--app-primary)",
      backgroundColor: "transparent",
      cursor: "pointer",
    },
  },
  repeatOn: {
    "& > p": { color: "var(--app-text-muted)", fontWeight: 400, margin: 0 },
    "& .dayCircleBox": {
      display: "flex",
      gap: "5px",
      marginTop: "10px",
      flexWrap: "wrap",
      "& .dayCircle": {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "50%",
        width: "25px",
        height: "25px",
        cursor: "pointer",
        "& p": { fontSize: "14px", fontWeight: 400, margin: 0, lineHeight: 1 },
      },
      "& .dayCircleActive": {
        backgroundColor: "var(--app-primary)",
        "& p": { color: "var(--app-on-primary)" },
      },
      "& .dayCircleInactive": {
        backgroundColor: "var(--app-surface-2)",
        "& p": { color: "var(--app-text)" },
      },
    },
  },
  timeslotsParent: {
    alignItems: "center",
    marginTop: 12,
    flexWrap: "wrap",
    maxHeight: 75,
    overflowY: "auto",
    gap: 4,
    "& > p": {
      margin: 0,
      padding: "2px 4px",
      backgroundColor: "var(--app-surface-2)",
      borderRadius: 6,
      fontSize: 12,
    },
  },
  noSpinner: {
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    MozAppearance: "textfield",
  },

  scheduleDetailsBox: {
    backgroundColor: "var(--app-surface-2)",
    marginTop: "13px",
    border: "1px solid var(--app-border)",
    borderRadius: "8px",
    padding: "18px 14px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    [theme.fn.smallerThan("sm")]: {
      padding: "12px 10px",
      gap: "10px",
    },
  },

  // ── Bulk upload wizard ──────────────────────────────────────────────────────
  wizardRail: {
    display: "flex",
    alignItems: "center",
    padding: "18px 32px",
    backgroundColor: "var(--app-surface)",
    borderBottom: "1px solid var(--app-border)",
    flexShrink: 0,
    [theme.fn.smallerThan("sm")]: { padding: "12px 16px" },
  },
  wizardStepItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  wizardCircle: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
  },
  wizardCircleActive: {
    backgroundColor: "var(--app-primary)",
    color: "var(--app-on-primary)",
    border: "1.5px solid var(--app-primary)",
    boxShadow: "0 0 0 5px rgba(255,186,69,0.18)",
  },
  wizardCircleComplete: {
    backgroundColor: "var(--app-primary)",
    color: "var(--app-on-primary)",
    border: "1.5px solid var(--app-primary)",
  },
  wizardCircleInactive: {
    backgroundColor: "var(--app-surface)",
    color: "var(--app-text-muted)",
    border: "1.5px solid var(--app-border)",
  },
  wizardLabel: {
    fontSize: 11,
    fontWeight: 700,
    fontFamily: "Inter",
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--app-text-muted)",
  },
  wizardLabelActive: { color: "var(--app-text)" },
  wizardLabelComplete: { color: "var(--app-text-muted)" },
  wizardConnector: {
    flex: 1,
    height: 2,
    backgroundColor: "var(--app-border)",
    marginBottom: 22,
    borderRadius: 2,
  },
  wizardConnectorFilled: { backgroundColor: "var(--app-primary)" },

  // ── Bulk file card ──────────────────────────────────────────────────────────
  bulkFileCard: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginTop: 8,
  },
  bulkFileCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  bulkFileCardLabel: {
    margin: 0,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    color: "var(--app-text-muted)",
    textTransform: "uppercase",
    fontFamily: "Inter",
  },
  bulkFileCardCount: {
    margin: 0,
    fontSize: 12,
    fontWeight: 600,
    color: "var(--app-text)",
    fontFamily: "Inter",
  },
  bulkFileCardCountMuted: {
    color: "var(--app-text-muted)",
    fontWeight: 500,
  },
  bulkFileProgressTrack: {
    height: 6,
    background: "var(--app-border)",
    borderRadius: 999,
    overflow: "hidden",
  },
  bulkFileProgressFill: {
    height: "100%",
    background: "linear-gradient(90deg, #FFAF4A, #FFBA45)",
    transition: "width .25s ease",
  },
  bulkFileInnerList: {
    display: "flex",
    flexDirection: "column",
  },
  bulkFileInnerRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 0",
    "&:not(:first-of-type)": { borderTop: "1px solid var(--app-border)" },
  },
  bulkFileIconBox: {
    width: 32,
    height: 32,
    borderRadius: 8,
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
  },
  bulkFileIconBoxImage: { background: "var(--app-tint-purple)", color: "var(--app-ink-purple)" },
  bulkFileIconBoxDoc: { background: "var(--app-tint-blue)", color: "var(--app-ink-blue)" },
  bulkFileIconBoxDone: { background: "var(--app-surface-2)", color: "var(--app-text-muted)" },
  bulkFileIconBoxFailed: { background: "var(--app-tint-red)", color: "var(--app-ink-red)" },
  bulkFileName: {
    flex: 1,
    minWidth: 0,
    margin: 0,
    fontSize: 13,
    fontWeight: 500,
    color: "var(--app-text)",
    fontFamily: "Inter",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  bulkFileSize: {
    fontSize: 11,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
    flexShrink: 0,
  },
  bulkFileStatus: {
    fontSize: 11,
    fontWeight: 600,
    fontFamily: "Inter",
    flexShrink: 0,
  },
  bulkFileStatusQueued: { color: "var(--app-text-muted)" },
  bulkFileStatusProcessing: { color: "var(--app-ink-purple)" },
  bulkFileStatusDone: { color: "var(--app-ink-green)" },
  bulkFileStatusFailed: { color: "var(--app-ink-red)" },
  bulkRemoveBtn: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    border: 0,
    background: "transparent",
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    color: "var(--app-text-muted)",
    "&:hover": { background: "var(--app-tint-red)", color: "var(--app-ink-red)" },
  },
  uploadFilesBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 12px",
    borderRadius: 8,
    border: "1px solid var(--app-border)",
    background: "var(--app-surface)",
    color: "var(--app-text)",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "Inter",
    cursor: "pointer",
    boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
    transition: "border-color .12s ease, background .12s ease",
    "&:hover": { borderColor: "var(--app-ink-purple)", color: "var(--app-ink-purple)" },
  },

  // ── Bulk review table ───────────────────────────────────────────────────────
  bulkReviewBanner: {
    marginBottom: 12,
    padding: "12px 14px",
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  bulkReviewBannerAlert: {
    background: "var(--app-tint-amber)",
    border: "1px solid var(--app-ink-amber)",
  },
  bulkReviewBannerOk: {
    background: "var(--app-tint-green)",
    border: "1px solid var(--app-ink-green)",
  },
  bulkReviewBannerDot: {
    width: 24,
    height: 24,
    borderRadius: 999,
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
  },
  bulkReviewBannerDotAlert: { background: "var(--app-tint-amber)" },
  bulkReviewBannerDotOk: { background: "var(--app-tint-green)" },
  bulkReviewBannerText: {
    margin: 0,
    fontSize: 13,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
    lineHeight: 1.45,
  },
  bulkReviewBannerStrong: {
    fontWeight: 700,
    color: "var(--app-text)",
  },
  bulkFilterChipsRow: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: 12,
  },
  bulkFilterChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "5px 6px 5px 12px",
    borderRadius: 999,
    background: "var(--app-surface)",
    color: "var(--app-text-muted)",
    border: "1px solid var(--app-border)",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "Inter",
    transition: "background .12s ease, border-color .12s ease",
  },
  bulkFilterChipActive: {
    background: "var(--app-text)",
    color: "var(--app-surface)",
    borderColor: "var(--app-text)",
  },
  bulkFilterChipBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 999,
    padding: "0 6px",
    background: "var(--app-surface-2)",
    color: "var(--app-text-muted)",
    fontWeight: 700,
    fontSize: 11,
    display: "inline-grid",
    placeItems: "center",
  },
  bulkFilterChipBadgeActive: {
    background: "var(--app-surface)",
    color: "var(--app-text)",
  },
  bulkReviewTableWrap: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    overflow: "hidden",
  },
  bulkReviewTableScroll: { overflowX: "auto" },
  bulkReviewTable: {
    width: "100%",
    minWidth: 900,
    borderCollapse: "collapse",
  },
  bulkReviewThead: {
    background: "var(--app-surface-2)",
    borderBottom: "1px solid var(--app-border)",
  },
  bulkReviewTh: {
    padding: "10px 12px",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 600,
    color: "var(--app-text-muted)",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    whiteSpace: "nowrap",
    fontFamily: "Inter",
  },
  bulkReviewTr: {
    borderBottom: "1px solid var(--app-border)",
    "&:last-child": { borderBottom: 0 },
    "&:hover": { background: "var(--app-surface-2)" },
  },
  bulkReviewTrSaved: {
    background: "var(--app-tint-green)",
    "&:hover": { background: "var(--app-tint-green)" },
  },
  bulkReviewTrSkipped: { opacity: 0.5 },
  bulkReviewTrDuplicate: { background: "var(--app-tint-amber)" },
  bulkReviewTd: {
    padding: "10px 12px",
    verticalAlign: "middle",
    fontSize: 13,
    color: "var(--app-text)",
    fontFamily: "Inter",
  },
  bulkReviewMedName: {
    margin: 0,
    fontSize: 13,
    fontWeight: 600,
    color: "var(--app-text)",
    fontFamily: "Inter",
  },
  bulkReviewMedSub: {
    margin: "2px 0 0",
    fontSize: 11,
    color: "var(--app-ink-amber)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 240,
  },
  bulkReviewMedNameRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  bulkReviewTinyBadge: {
    display: "inline-flex",
    alignItems: "center",
    height: 16,
    padding: "0 6px",
    borderRadius: 999,
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 0.3,
    fontFamily: "Inter",
    flexShrink: 0,
  },
  bulkReviewBadgeNarcotic: {
    background: "var(--app-tint-red)",
    color: "var(--app-ink-red)",
  },
  bulkReviewBadgePrn: {
    background: "#F3E8FF",
    color: "#6B21A8",
  },
  bulkReviewSourceBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  bulkReviewSourceIcon: {
    width: 26,
    height: 26,
    borderRadius: 6,
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    background: "var(--app-tint-blue)",
    color: "var(--app-ink-blue)",
  },
  bulkReviewSourceName: {
    fontSize: 12,
    color: "var(--app-text)",
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 150,
    display: "block",
  },
  bulkStatusChip: {
    display: "inline-flex",
    alignItems: "center",
    height: 20,
    padding: "0 8px",
    borderRadius: 10,
    fontSize: 10,
    fontWeight: 700,
    fontFamily: "Inter",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  bulkStatusReady: { background: "var(--app-tint-green)", color: "var(--app-ink-green)" },
  bulkStatusFailed: { background: "var(--app-tint-red)", color: "var(--app-ink-red)" },
  bulkStatusSaved: { background: "var(--app-tint-blue)", color: "var(--app-ink-blue)" },
  bulkStatusSkipped: { background: "var(--app-surface-2)", color: "var(--app-text-muted)" },
  bulkStatusDuplicate: { background: "var(--app-tint-amber)", color: "var(--app-ink-amber)" },
  bulkStatusNeedsReview: { background: "var(--app-tint-amber)", color: "var(--app-ink-amber)" },
  bulkReviewActionsCell: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  bulkReviewBtnEdit: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "6px",
    height: "fit-content",
    borderRadius: 6,
    background: "transparent",
    border: "1px solid var(--app-border)",
    color: "var(--app-text-muted)",
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "Inter",
    "&:hover": { borderColor: "var(--app-text-muted)", color: "var(--app-text)" },
  },
  bulkReviewBtnReview: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "0 10px",
    height: 26,
    borderRadius: 6,
    background: "transparent",
    border: "1px solid var(--app-ink-amber)",
    color: "var(--app-ink-amber)",
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "Inter",
    "&:hover": { background: "var(--app-tint-amber)" },
  },
  bulkReviewBtnSkip: {
    width: 26,
    height: 26,
    borderRadius: 6,
    background: "transparent",
    border: "1px solid var(--app-ink-red)",
    color: "var(--app-ink-red)",
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    "&:hover": { borderColor: "var(--app-ink-red)", background: "var(--app-tint-red)" },
  },
  bulkReviewCountLine: {
    margin: "10px 0 0",
    fontSize: 12,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
  },

  // ── Bulk row inline review (BulkRowFlow) ─────────────────────────────────────
  brFlow: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  brHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  brBack: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "transparent",
    border: "none",
    padding: "4px 0",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "Inter",
    color: "var(--app-text-muted)",
    cursor: "pointer",
    "&:hover": { color: "var(--app-text)" },
  },
  brProgress: {
    fontSize: 12,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
  },
  brReasonCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid",
  },
  brReasonCardDupe: {
    background: "var(--app-tint-amber)",
    borderColor: "var(--app-ink-amber)",
    color: "var(--app-ink-amber)",
  },
  brReasonCardNeedsReview: {
    background: "var(--app-tint-amber)",
    borderColor: "var(--app-ink-amber)",
    color: "var(--app-ink-amber)",
  },
  brReasonCardFailed: {
    background: "var(--app-tint-red)",
    borderColor: "var(--app-ink-red)",
    color: "var(--app-ink-red)",
  },
  brReasonCardIcon: {
    flexShrink: 0,
    marginTop: 1,
  },
  brReasonCardTitle: {
    margin: "0 0 2px",
    fontSize: 13,
    fontWeight: 700,
    fontFamily: "Inter",
  },
  brReasonCardDesc: {
    margin: 0,
    fontSize: 12,
    fontFamily: "Inter",
    lineHeight: 1.5,
  },
  brRowCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 10,
    padding: "14px 14px",
  },
  brRowCardInfo: {
    flex: 1,
    minWidth: 0,
  },
  brRowCardName: {
    margin: "0 0 6px",
    fontSize: 14,
    fontWeight: 700,
    color: "var(--app-text)",
    fontFamily: "Inter",
  },
  brRowCardMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px 14px",
    marginBottom: 6,
  },
  brRowCardMetaItem: {
    fontSize: 12,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
  },
  brRowCardMetaLabel: {
    fontWeight: 600,
    color: "var(--app-text-muted)",
    marginRight: 3,
  },
  brRowCardSource: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
  },
  brRowCardActions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  brIconBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "1px solid",
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    background: "var(--app-surface)",
    transition: "background .1s, border-color .1s",
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  brIconBtnEdit: {
    borderColor: "var(--app-border)",
    color: "var(--app-text)",
    "&:hover": { background: "var(--app-surface-2)", borderColor: "var(--app-text-muted)" },
  },
  brIconBtnAccept: {
    borderColor: "var(--app-ink-green)",
    color: "var(--app-ink-green)",
    "&:hover": { background: "var(--app-tint-green)", borderColor: "var(--app-ink-green)" },
  },
  brIconBtnDelete: {
    borderColor: "var(--app-ink-red)",
    color: "var(--app-ink-red)",
    "&:hover": { background: "var(--app-tint-red)", borderColor: "var(--app-ink-red)" },
  },
  brBtnSkip: {
    alignSelf: "flex-start",
    background: "transparent",
    border: "1px solid var(--app-border)",
    borderRadius: 8,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "Inter",
    color: "var(--app-text)",
    cursor: "pointer",
    "&:hover": { background: "var(--app-surface-2)" },
  },
  brInnerMedCard: {
    display: "flex",
    alignItems: "stretch",
    gap: 12,
    padding: "12px 14px",
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 10,
  },
  brInnerMedCardBody: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  brInnerMedCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  brInnerMedCardName: {
    margin: 0,
    fontSize: 14,
    fontWeight: 700,
    color: "var(--app-text)",
    fontFamily: "Inter",
  },
  brInnerMedCardDetail: {
    margin: 0,
    fontSize: 12,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
    lineHeight: 1.4,
  },
  brInnerMedCardSource: {
    margin: 0,
    fontSize: 11,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
  },
  brChip: {
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.3,
    fontFamily: "Inter",
    lineHeight: "18px",
  },
  brChipNeedsReview: {
    background: "var(--app-tint-amber)",
    color: "var(--app-ink-amber)",
  },
  brChipNarcotic: {
    background: "var(--app-tint-red)",
    color: "var(--app-ink-red)",
  },
  brChipActiveList: {
    background: "#F0FDF4",
    color: "#15803D",
  },
  brChipDuplicate: {
    background: "#FFFBF1",
    color: "#92400E",
  },
  singleScanActiveCard: {
    background: "var(--app-tint-amber)",
    border: "1px solid var(--app-ink-amber)",
    borderRadius: 12,
    padding: 16,
    marginBottom: 4,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  singleScanActiveCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
  },
  singleScanActiveCardIcon: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    flexShrink: 0,
    background: "var(--app-tint-amber)",
    display: "grid",
    placeItems: "center",
  },
  singleScanActiveCardText: {
    flex: 1,
    minWidth: 0,
  },
  singleScanActiveCardLabel: {
    margin: 0,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    color: "var(--app-ink-amber)",
    textTransform: "uppercase",
    fontFamily: "Inter",
  },
  singleScanActiveCardBody: {
    margin: "4px 0 0",
    fontSize: 14,
    color: "var(--app-text)",
    lineHeight: 1.5,
    fontFamily: "Inter",
  },
  brStackWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  brLockedCard: {
    display: "flex",
    alignItems: "stretch",
    gap: 12,
    padding: "12px 14px",
    background: "var(--app-surface-2)",
    border: "1px solid var(--app-border)",
    borderRadius: 10,
  },
  brSelectAllRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    paddingInline: 4,
  },
  brSelectAllLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    fontWeight: 600,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
    cursor: "pointer",
  },
  brSelectAllCount: {
    fontSize: 12,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
  },
  brDupeCheckbox: {
    accentColor: "var(--app-primary)",
    width: 14,
    height: 14,
  },
  brStackedRowCard: {
    display: "flex",
    alignItems: "stretch",
    gap: 12,
    padding: "12px 14px",
    background: "var(--app-surface)",
    borderRadius: 10,
    border: "1px solid var(--app-border)",
    transition: "border-color .14s ease, box-shadow .14s ease",
  },
  brStackedRowCardSelected: {
    border: "1px solid var(--app-primary)",
    boxShadow: "0 0 0 3px rgba(255,175,74,.15)",
  },
  brStackedRowCheckboxWrap: {
    display: "flex",
    alignItems: "flex-start",
    paddingTop: 2,
  },
  brCombineRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 2,
  },
  brCombineBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 36,
    padding: "0 16px",
    borderRadius: 8,
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 700,
    transition: "background .12s ease, border-color .12s ease",
  },
  brCombineBtnActive: {
    border: "1px solid var(--app-ink-green)",
    background: "var(--app-tint-green)",
    color: "var(--app-ink-green)",
    cursor: "pointer",
  },
  brCombineBtnDisabled: {
    border: "1px solid var(--app-border)",
    background: "var(--app-surface-2)",
    color: "var(--app-text-muted)",
    cursor: "not-allowed",
  },

  // ── Bulk save preview screen ─────────────────────────────────────────────────
  bulkConfirmWrap: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  bulkConfirmTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: "var(--app-text)",
    fontFamily: "Inter",
  },
  bulkConfirmSub: {
    margin: "4px 0 0",
    fontSize: 13,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
  },
  bulkConfirmList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  bulkConfirmRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 14px",
    border: "1px solid var(--app-border)",
    borderRadius: 10,
    background: "var(--app-surface-2)",
  },
  bulkConfirmCheckCircle: {
    width: 28,
    height: 28,
    borderRadius: 999,
    flexShrink: 0,
    background: "var(--app-tint-green)",
    display: "grid",
    placeItems: "center",
  },
  bulkConfirmMedInfo: {
    flex: 1,
    minWidth: 0,
  },
  bulkConfirmMedName: {
    margin: 0,
    fontSize: 14,
    fontWeight: 700,
    color: "var(--app-text)",
    fontFamily: "Inter",
  },
  bulkConfirmMedDetails: {
    margin: "2px 0 0",
    fontSize: 12,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
  },
  bulkConfirmNarcBadge: {
    padding: "2px 8px",
    borderRadius: 999,
    background: "var(--app-tint-red)",
    color: "var(--app-ink-red)",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.3,
    fontFamily: "Inter",
    flexShrink: 0,
  },
  bulkConfirmSourceText: {
    margin: "2px 0 0",
    fontSize: 11,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  bulkConfirmEditBtn: {
    width: 32,
    height: 32,
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    color: "var(--app-text-muted)",
    cursor: "pointer",
    padding: 0,
    transition: "background .12s ease, border-color .12s ease",
    "&:hover": { background: "var(--app-surface-2)", borderColor: "var(--app-text-muted)" },
  },

  // ── Bulk saved screen ────────────────────────────────────────────────────────
  bulkSavedWrap: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    padding: 36,
    maxWidth: 560,
    margin: "32px auto 0",
    textAlign: "center",
  },
  bulkSavedIcon: {
    width: 72,
    height: 72,
    borderRadius: 999,
    background: "var(--app-tint-green)",
    border: "1px solid var(--app-ink-green)",
    display: "grid",
    placeItems: "center",
    margin: "0 auto 18px",
  },
  bulkSavedTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "var(--app-text)",
    fontFamily: "Inter",
  },
  bulkSavedSub: {
    margin: "8px 0 0",
    fontSize: 13,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
    lineHeight: 1.5,
  },

  /* ── Bulk row edit: source file card ───────────────────── */
  bulkRowSourceCard: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    padding: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
  },
  bulkRowSourceLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  bulkRowSourceThumb: {
    width: 56,
    height: 72,
    borderRadius: 6,
    flexShrink: 0,
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    cursor: "default",
    overflow: "hidden",
    position: "relative",
    boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    boxSizing: "border-box",
    padding: "6px 6px",
  },
  bulkRowSourceThumbLine0: {
    height: 4,
    width: "70%",
    background: "var(--app-text)",
    borderRadius: 2,
    flexShrink: 0,
  },
  bulkRowSourceThumbLine: {
    height: 3,
    background: "var(--app-border)",
    borderRadius: 2,
    flexShrink: 0,
  },
  bulkRowSourceInfo: {
    flex: 1,
    minWidth: 0,
  },
  bulkRowSourceFileName: {
    margin: 0,
    fontSize: 13,
    fontWeight: 700,
    color: "var(--app-text)",
    fontFamily: "Inter",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  bulkRowSourceMeta: {
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
  },
  bulkRowSourceSep: {
    color: "var(--app-border)",
  },
  bulkRowSourceStats: {
    display: "flex",
    gap: 18,
    alignItems: "center",
    flexWrap: "wrap",
    flex: "1 1 auto",
  },
  bulkRowStatItem: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  bulkRowStatLabel: {
    margin: 0,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.5,
    color: "var(--app-text-muted)",
    textTransform: "uppercase",
    fontFamily: "Inter",
  },
  bulkRowStatValue: {
    margin: 0,
    fontSize: 13,
    fontWeight: 600,
    color: "var(--app-text)",
    fontFamily: "Inter",
  },
  bulkRowStatusReady: {
    padding: "3px 9px",
    borderRadius: 999,
    background: "var(--app-tint-green)",
    border: "1px solid var(--app-ink-green)",
    color: "var(--app-ink-green)",
    fontSize: 11,
    fontWeight: 700,
    fontFamily: "Inter",
  },
  bulkRowStatusDuplicate: {
    padding: "3px 9px",
    borderRadius: 999,
    background: "var(--app-tint-amber)",
    border: "1px solid var(--app-ink-amber)",
    color: "var(--app-ink-amber)",
    fontSize: 11,
    fontWeight: 700,
    fontFamily: "Inter",
  },
  bulkRowStatusNeedsReview: {
    padding: "3px 9px",
    borderRadius: 999,
    background: "var(--app-tint-amber)",
    border: "1px solid var(--app-ink-amber)",
    color: "var(--app-ink-amber)",
    fontSize: 11,
    fontWeight: 700,
    fontFamily: "Inter",
  },
  bulkRowStatusFailed: {
    padding: "3px 9px",
    borderRadius: 999,
    background: "var(--app-tint-red)",
    border: "1px solid var(--app-ink-red)",
    color: "var(--app-ink-red)",
    fontSize: 11,
    fontWeight: 700,
    fontFamily: "Inter",
  },
}));

export default useStyles;
